import React, { useEffect, Component } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPinIcon, PhoneIcon, MailIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
// Fix for default marker icons in React Leaflet
const defaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});
// Custom icon for the Embassy (red/gold to stand out)
const embassyIcon = L.icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});
// Component to fit map bounds to all markers
function FitBounds({
  consulates
}: {
  consulates: ConsulateData[];
}) {
  const map = useMap();
  useEffect(() => {
    if (consulates.length > 0) {
      const bounds = L.latLngBounds(consulates.map(c => [c.lat, c.lng] as [number, number]));
      map.fitBounds(bounds, {
        padding: [30, 30],
        maxZoom: 5
      });
    }
  }, [map, consulates]);
  return null;
}
export interface ConsulateData {
  name: string;
  nameEs?: string;
  address: string;
  phone: string;
  email?: string;
  isEmbassy?: boolean;
  facebook?: string;
  lat: number;
  lng: number;
}
interface ConsulateMapProps {
  consulates: ConsulateData[];
  accentColor: string;
  expanded?: boolean;
}
export function ConsulateMap({
  consulates,
  accentColor,
  expanded = false
}: ConsulateMapProps) {
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  return <div className="w-full relative z-0" style={{
    height: expanded ? '65vh' : '280px'
  }} data-id="element-1291">
      <MapContainer center={[39.0, -98.0]} zoom={4} scrollWheelZoom={expanded} className="w-full h-full" data-id="element-1292">
        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-1293" />
        <FitBounds consulates={consulates} data-id="element-1294" />
        {consulates.map((consulate, index) => <Marker key={index} position={[consulate.lat, consulate.lng]} icon={consulate.isEmbassy ? embassyIcon : defaultIcon} data-id="element-1295">
            <Popup className="font-nunito" data-id="element-1296">
              <div className="flex flex-col gap-1.5 min-w-[200px]" data-id="element-1297">
                <h4 className={`font-bold text-sm m-0 ${accentColor}`} data-id="element-1298">
                  {isEs && consulate.nameEs ? consulate.nameEs : consulate.name}
                </h4>

                <div className="flex items-start gap-1.5 mt-1" data-id="element-1299">
                  <MapPinIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" data-id="element-1300" />
                  <span className="text-xs text-gray-600 leading-tight m-0" data-id="element-1301">
                    {consulate.address}
                  </span>
                </div>

                <div className="flex items-center gap-1.5" data-id="element-1302">
                  <PhoneIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" data-id="element-1303" />
                  <a href={`tel:${consulate.phone.replace(/-/g, '')}`} className={`text-xs font-semibold m-0 ${accentColor} hover:underline`} data-id="element-1304">
                    {consulate.phone}
                  </a>
                </div>

                {consulate.email && <div className="flex items-center gap-1.5" data-id="element-1305">
                    <MailIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" data-id="element-1306" />
                    <a href={`mailto:${consulate.email}`} className={`text-xs font-semibold m-0 ${accentColor} hover:underline`} data-id="element-1307">
                      {consulate.email}
                    </a>
                  </div>}

                {consulate.facebook && <div className="flex items-center gap-1.5 mt-1 pt-1.5 border-t border-gray-100" data-id="element-1308">
                    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 fill-current" aria-hidden="true" data-id="element-1309">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" data-id="element-1310" />
                    </svg>
                    <a href={consulate.facebook} target="_blank" rel="noopener noreferrer" className={`text-xs font-semibold m-0 ${accentColor} hover:underline`} data-id="element-1311">
                      Facebook
                    </a>
                  </div>}
              </div>
            </Popup>
          </Marker>)}
      </MapContainer>
    </div>;
}